// A car's settings as its TUNE MFD's menu has them (TUNE Live's tune-ui screens/settings.rs and datalogs.rs): Units,
// Vehicle, Service (on its Trips and service page) and Datalogs, in its order and words. Numbers are kept metric and
// shown in the car's units, rounded as the MFD rounds them.
import { fromDisplay, symbol, toDisplay, type Units } from '@tune/shared';

export interface Choice {
  value: string | number | boolean;
  label: string;
}

const plain = (values: Array<string | number>): Choice[] => values.map((v) => ({ value: v, label: String(v) }));

/** A setting as the MFD's menu shows it. */
export type Field =
  | { key: string; label: string; kind: 'choice'; choices: Choice[] }
  | { key: string; label: string; kind: 'segments'; choices: Choice[] }
  | { key: string; label: string; kind: 'number'; unit: 'rpm' | 'mm' | 'h' | 'speed' | 'distance' | null }
  | { key: 'gearRatios'; label: string; kind: 'gears' }
  | { key: 'lockWhileDriving'; label: string; kind: 'switch' }
  | { key: 'logValues'; label: string; kind: 'logValues' };

export type NumberField = Field & { kind: 'number' };

export const UNITS: Field[] = [
  { key: 'speedUnit', label: 'Speed', kind: 'choice', choices: plain(['km/h', 'mph', 'm/s', 'knots']) },
  { key: 'distanceUnit', label: 'Distance', kind: 'choice', choices: plain(['km', 'miles']) },
  { key: 'temperatureUnit', label: 'Temperature', kind: 'choice', choices: plain(['°C', '°F']) },
  { key: 'pressureUnit', label: 'Pressure', kind: 'choice', choices: plain(['bar', 'psi', 'kPa']) },
  {
    key: 'consumptionUnit',
    label: 'Fuel consumption',
    kind: 'choice',
    choices: plain(['L/100 km', 'mpg (US)', 'mpg (UK)', 'km/L']),
  },
  {
    key: 'dateFormat',
    label: 'Date',
    kind: 'choice',
    choices: plain(['31/12/2026', '12/31/2026', '2026-12-31', '31.12.2026']),
  },
  { key: 'timeFormat', label: 'Time', kind: 'choice', choices: plain(['17:30', '5:30', '5:30 PM']) },
];

export const VEHICLE: Field[] = [
  { key: 'redline', label: 'Redline', kind: 'number', unit: 'rpm' },
  { key: 'rpmWarning', label: 'RPM warning from', kind: 'number', unit: 'rpm' },
  { key: 'rpmCritical', label: 'RPM critical from', kind: 'number', unit: 'rpm' },
  { key: 'topSpeed', label: 'Top speed', kind: 'number', unit: 'speed' },
  {
    key: 'speedFrom',
    label: 'Speed from',
    kind: 'segments',
    choices: [
      { value: 'gps', label: 'GPS' },
      { value: 'ecu', label: 'ECU' },
    ],
  },
  { key: 'finalDrive', label: 'Final drive', kind: 'number', unit: null },
  { key: 'tyreCircumferenceMm', label: 'Tyre circumference', kind: 'number', unit: 'mm' },
  { key: 'gearRatios', label: 'Gear ratios', kind: 'gears' },
  {
    key: 'gearFrom',
    label: 'Gear from',
    kind: 'segments',
    choices: [
      { value: 'calculated', label: 'Calculated' },
      { value: 'ecu', label: 'ECU' },
    ],
  },
  { key: 'lockWhileDriving', label: 'Lock the menu while driving', kind: 'switch' },
];

export const REMIND_BY: Field = {
  key: 'serviceByHours',
  label: 'Remind by',
  kind: 'segments',
  choices: [
    { value: false, label: 'Distance' },
    { value: true, label: 'Engine hours' },
  ],
};
export const EVERY_KM: NumberField = { key: 'serviceIntervalKm', label: 'Every', kind: 'number', unit: 'distance' };
export const EVERY_HOURS: NumberField = { key: 'serviceIntervalHours', label: 'Every', kind: 'number', unit: 'h' };

export const RATE: Field = {
  key: 'logRate',
  label: 'Samples a second',
  kind: 'segments',
  choices: plain([20, 50, 100]),
};
export const LOG_VALUES: Field = { key: 'logValues', label: 'Values', kind: 'logValues' };

export const ALL: Field[] = [...UNITS, ...VEHICLE, REMIND_BY, EVERY_KM, EVERY_HOURS, RATE, LOG_VALUES];

/** What the MFD says to a number that isn't one, or isn't above 0. */
export const ABOVE_ZERO = 'That needs a number above 0';

/** The most gears, and what a ratio must be. */
export const MOST_GEARS = 10;

/** "1st", "2nd", "11th". */
export function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) {
    return `${n}th`;
  }
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
}

/** The unit a number shows, in these units. */
export function unitOf(field: NumberField, units: Units): string | null {
  if (field.unit === 'speed') {
    return symbol(units, 'speed');
  }
  if (field.unit === 'distance') {
    return symbol(units, 'distance');
  }
  return field.unit;
}

/** A stored number as its field shows it: in the car's units, and rounded as the MFD rounds it. */
export function shown(field: NumberField, units: Units, stored: number): string {
  if (field.unit === 'speed') {
    return String(Math.round(toDisplay(units, 'speed', stored)));
  }
  if (field.unit === 'distance') {
    return String(Math.round(toDisplay(units, 'distance', stored)));
  }
  return String(Math.round(stored * 1000) / 1000);
}

/** A number as typed ("1,250" or "0,8" too), or NaN when it isn't one. */
export function typedNumber(typed: string): number {
  const t = typed.trim();
  // A comma between digits groups thousands ("1,250"); one before the last one or two digits is a decimal point.
  const n = Number(/^\d+,\d{1,2}$/.test(t) ? t.replace(',', '.') : t.replace(/,/g, ''));
  return t === '' || !Number.isFinite(n) ? Number.NaN : n;
}

/** What's typed, as it's kept: back from the car's units. NaN when it isn't a number. */
export function stored(field: NumberField, units: Units, typed: string): number {
  const n = typedNumber(typed);
  if (Number.isNaN(n)) {
    return n;
  }
  if (field.unit === 'speed') {
    return fromDisplay(units, 'speed', n);
  }
  if (field.unit === 'distance') {
    return fromDisplay(units, 'distance', n);
  }
  return n;
}

/** A value in words, as the history says it. */
export function describe(field: Field, value: unknown, units: Units): string {
  if (value === null || value === undefined) {
    return field.kind === 'logValues' ? 'Everything the car sends' : 'Not set';
  }
  switch (field.kind) {
    case 'choice':
    case 'segments':
      return field.choices.find((c) => c.value === value)?.label ?? String(value);
    case 'number': {
      const unit = unitOf(field, units);
      return typeof value === 'number' ? `${shown(field, units, value)}${unit ? ` ${unit}` : ''}` : String(value);
    }
    case 'gears':
      return Array.isArray(value) ? value.join(' · ') : String(value);
    case 'switch':
      return value === true ? 'On' : 'Off';
    case 'logValues':
      return Array.isArray(value) ? (value.length === 1 ? '1 value' : `${value.length} values`) : String(value);
  }
}

/** A field by its key, and what the history calls it. */
export function fieldOf(key: string): Field | undefined {
  return ALL.find((f) => f.key === key);
}

export function historyLabel(key: string): string {
  if (key === 'serviceIntervalKm' || key === 'serviceIntervalHours') {
    return 'Service every';
  }
  if (key === 'logValues') {
    return 'Datalog values';
  }
  return fieldOf(key)?.label ?? key;
}

/** What's wrong with a changed value, in the MFD's words, or null. */
export function problemWith(key: string, value: unknown): string | null {
  if (key === 'gearRatios') {
    return Array.isArray(value) && value.every((r) => typeof r === 'number' && r > 0 && r < 100)
      ? null
      : 'Each ratio needs a number above 0 and below 100';
  }
  if (typeof value === 'number' && !(value > 0 && value < 1e7)) {
    return ABOVE_ZERO;
  }
  return null;
}
